import * as THREE from 'three';
import { clamp, lerp, smoothDamp } from '../shared/utils';

export interface CameraShake {
  intensity: number;
  duration: number;
  elapsed: number;
}

export class CameraController {
  public yaw = 0;
  public pitch = 0;
  public targetFov = 76;
  public currentFov = 76;
  public fovSmoothing = 8;

  private camera: THREE.PerspectiveCamera;
  private shakeOffset = new THREE.Vector3();
  private shakeRotation = new THREE.Euler();
  private shakes: CameraShake[] = [];
  private currentShakeIntensity = 0;
  private velocityY = { value: 0 };
  private velocityX = { value: 0 };
  private smoothTime = 0.04;
  private positionSmoothTime = 0.02;

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
  }

  addShake(intensity: number, duration: number): void {
    this.shakes.push({ intensity, duration, elapsed: 0 });
  }

  update(dt: number, targetPos: THREE.Vector3, yaw: number, pitch: number): void {
    this.yaw = smoothDamp(this.yaw, yaw, this.velocityY, this.smoothTime, dt);
    this.pitch = smoothDamp(this.pitch, pitch, this.velocityX, this.smoothTime, dt);

    this.currentFov = lerp(this.currentFov, this.targetFov, dt * this.fovSmoothing);
    this.camera.fov = this.currentFov;
    this.camera.updateProjectionMatrix();

    let shakeX = 0, shakeY = 0, shakeZ = 0;
    let shakeRotX = 0, shakeRotY = 0;
    this.currentShakeIntensity = 0;

    for (let i = this.shakes.length - 1; i >= 0; i--) {
      const s = this.shakes[i];
      s.elapsed += dt;
      if (s.elapsed >= s.duration) {
        this.shakes.splice(i, 1);
        continue;
      }
      const t = 1 - s.elapsed / s.duration;
      const intensity = s.intensity * t;
      this.currentShakeIntensity = Math.max(this.currentShakeIntensity, intensity);
      shakeX += (Math.random() * 2 - 1) * intensity * 0.02;
      shakeY += (Math.random() * 2 - 1) * intensity * 0.02;
      shakeZ += (Math.random() * 2 - 1) * intensity * 0.01;
      shakeRotX += (Math.random() * 2 - 1) * intensity * 0.003;
      shakeRotY += (Math.random() * 2 - 1) * intensity * 0.003;
    }

    this.shakeOffset.set(shakeX, shakeY, shakeZ);
    this.shakeRotation.set(shakeRotX, shakeRotY, 0);

    this.camera.position.copy(targetPos);
    this.camera.position.add(this.shakeOffset);
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    this.camera.rotation.z = 0;
  }

  setFOV(fov: number): void {
    this.targetFov = clamp(fov, 60, 120);
  }

  reset(): void {
    this.yaw = 0;
    this.pitch = 0;
    this.currentFov = this.targetFov;
    this.shakes = [];
    this.shakeOffset.set(0, 0, 0);
  }
}
